import React, { useState, useEffect, useRef } from "react";
import { db, storage } from "./firebaseConfig";
import { collection, addDoc, serverTimestamp, getDocs } from "firebase/firestore";
import { createQuizApi } from "./utils/quizCreationApi";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { useNavigate } from "react-router-dom";
import { getAuth } from "firebase/auth";
import {
  ArrowLeft, BookOpenCheck, CheckCircle2, ClipboardCheck, ImagePlus,
  LoaderCircle, Save, Sparkles, Trash2, WandSparkles,
} from "lucide-react";
import { accountDisplayName, DashboardHeader, DashboardSidebar } from "./Dashboard";
import MultipleChoiceEditor from "./components/MultipleChoiceEditor";
import {
  QUESTION_TYPE_MULTIPLE_CHOICE,
  QUESTION_TYPE_SHORT_ANSWER,
  getEffectiveQuestionType,
  validateMcqQuestion,
} from "./utils/mcqQuestion";
import "./GenerateQuizQuestions.css";
import { API_BASE_URL } from "./utils/apiBase";

// Backend API URL - uses environment variable with localhost fallback for development

const DIFFICULTY_LEVELS = ["Easy", "Medium", "Hard", "Mixed"];
export const GENERATION_TYPE_MIXED = "mixed";

export const mixedQuestionSplit = (total) => ({
  multipleChoice: Math.ceil(total / 2),
  shortAnswer: Math.floor(total / 2),
});

export const nextMixedQuestionType = (questions) => {
  const target = mixedQuestionSplit(questions.length + 1);
  const multipleChoiceCount = questions.filter((question) => question.questionType === QUESTION_TYPE_MULTIPLE_CHOICE).length;
  return multipleChoiceCount < target.multipleChoice ? QUESTION_TYPE_MULTIPLE_CHOICE : QUESTION_TYPE_SHORT_ANSWER;
};

const QUESTION_TYPE_LABELS = Object.freeze({
  shortAnswer: "Short Answer",
  multipleChoice: "Multiple Choice",
  trueFalse: "True / False",
  longAnswer: "Long Answer",
  fillBlank: "Fill in the Blank",
  numerical: "Numerical",
  essay: "Essay",
});

const GENERATION_STATUS_STEPS = [
  { delay: 0, message: "Hold tight... putting together your questions" },
  { delay: 6000, message: "Building the assessment structure" },
  { delay: 14000, message: "Refining questions and mark schemes" },
  { delay: 24000, message: "Checking everything looks right" },
  { delay: 38000, message: "Nearly there... finalising your assessment" }
];

const normaliseTextValue = (value) => {
  if (typeof value === "string") return value.trim();
  if (typeof value === "number" || typeof value === "boolean") return String(value).trim();
  return "";
};

const questionTypeDisplayLabel = (value) => {
  const type = normaliseTextValue(value) || "shortAnswer";
  if (QUESTION_TYPE_LABELS[type]) return QUESTION_TYPE_LABELS[type];
  return type
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
};

const ensureMarkTag = (questionText, maxMark) => {
  let cleaned = normaliseTextValue(questionText);
  const markNum = parseInt(maxMark, 10) || 1;

  cleaned = cleaned
    .replace(/^(\s*(?:\[|\()\s*\d+\s*marks?\s*(?:\]|\))\s*[:-\u2013\u2014.]?\s*)+/i, "")
    .replace(/^\s*\d+\s*marks?\s*[:-\u2013\u2014.]\s*/i, "")
    .trim();

  const trailingMarkTagRegex = /\s*(?:(?:\[|\()\s*\d+\s*marks?\s*(?:\]|\))|(?:\d+\s*marks?))\s*[.?!]?\s*$/i;
  while (trailingMarkTagRegex.test(cleaned)) {
    cleaned = cleaned.replace(trailingMarkTagRegex, "").trim();
  }

  if (!cleaned) return "";

  const label = markNum === 1 ? "Mark" : "Marks";
  return `${cleaned} [${markNum} ${label}]`;
};

const normaliseMarkSchemeAnswer = (value) => {
  if (typeof value === "string") return value.trim();

  if (Array.isArray(value)) {
    return value
      .map(item => {
        if (typeof item === "string") return item;
        if (typeof item === "number" || typeof item === "boolean") return String(item);
        if (item && typeof item === "object") return Object.values(item).map(normaliseMarkSchemeAnswer).filter(Boolean).join(" ");
        return "";
      })
      .filter(Boolean)
      .join("\n")
      .trim();
  }

  if (value && typeof value === "object") {
    return Object.values(value)
      .map(v => {
        if (typeof v === "string") return v;
        if (typeof v === "number" || typeof v === "boolean") return String(v);
        if (Array.isArray(v)) return v.map(normaliseMarkSchemeAnswer).filter(Boolean).join(" ");
        if (v && typeof v === "object") return Object.values(v).map(normaliseMarkSchemeAnswer).filter(Boolean).join(" ");
        return "";
      })
      .filter(Boolean)
      .join("\n")
      .trim();
  }

  return "";
};

const normaliseOptions = (options) => {
  if (!Array.isArray(options)) return [];
  return options
    .map(option => {
      if (!option || typeof option !== "object" || Array.isArray(option)) return null;
      return { id: normaliseTextValue(option.id), text: normaliseTextValue(option.text) };
    })
    .filter(Boolean);
};

export const normaliseGeneratedQuestion = (question, idx, idPrefix = "gen") => {
  const q = question || {};
  const questionType = getEffectiveQuestionType(q.questionType || q.type);
  if (questionType === QUESTION_TYPE_MULTIPLE_CHOICE) {
    return {
      questionType,
      questionText: normaliseTextValue(q.questionText),
      options: normaliseOptions(q.options),
      correctOptionId: normaliseTextValue(q.correctOptionId),
      maxMark: 1,
      questionImageURL: normaliseTextValue(q.questionImageURL),
      _id: q._id || `${idPrefix}_${idx}_${Date.now()}`,
    };
  }
  const maxMark = parseInt(q.maxMark ?? q.marks ?? q.mark, 10) || 1;
  const answer = normaliseMarkSchemeAnswer(q.answer || q.modelAnswer || q.studentModelAnswer || "");
  const markSchemeAnswer = normaliseMarkSchemeAnswer(
    q.markSchemeAnswer || q.markScheme || q.modelAnswer || q.studentModelAnswer || q.answer
  );

  return {
    ...q,
    questionText: ensureMarkTag(normaliseTextValue(q.questionText), maxMark),
    answer,
    markSchemeAnswer,
    studentModelAnswer: normaliseMarkSchemeAnswer(q.studentModelAnswer || q.modelAnswer || q.answer || markSchemeAnswer),
    modelAnswer: normaliseMarkSchemeAnswer(q.modelAnswer || q.studentModelAnswer || q.answer || markSchemeAnswer),
    maxMark,
    questionType: QUESTION_TYPE_SHORT_ANSWER,
    options: [],
    questionImageURL: normaliseTextValue(q.questionImageURL),
    _id: q._id || `${idPrefix}_${idx}_${Date.now()}`
  };
};

export const buildGeneratedQuestionFields = (question, metadata = {}) => {
  const normalised = normaliseGeneratedQuestion(question, 0);
  const common = {
    questionText: normalised.questionText,
    topic: normaliseTextValue(metadata.topic),
    subject: normaliseTextValue(metadata.subject),
    level: normaliseTextValue(metadata.level),
    specCode: normaliseTextValue(metadata.specCode),
    difficulty: normaliseTextValue(metadata.difficulty),
  };
  if (normalised.questionImageURL) common.questionImageURL = normalised.questionImageURL;

  if (normalised.questionType === QUESTION_TYPE_MULTIPLE_CHOICE) {
    const validation = validateMcqQuestion(normalised);
    if (!validation.isValid) {
      const error = new Error(validation.errors.join(" "));
      error.validationErrors = validation.errors;
      throw error;
    }
    return { ...common, ...validation.value };
  }

  return {
    ...common,
    answer: normalised.answer || normalised.modelAnswer || normalised.markSchemeAnswer,
    markSchemeAnswer: normalised.markSchemeAnswer,
    studentModelAnswer: normalised.studentModelAnswer,
    modelAnswer: normalised.modelAnswer,
    maxMark: normalised.maxMark,
    questionType: QUESTION_TYPE_SHORT_ANSWER,
    options: [],
  };
};

export const buildGenerationPayload = (formData) => ({
  subject: formData.subject,
  topic: formData.topic,
  level: formData.level,
  specCode: formData.specCode,
  numQuestions: formData.numQuestions,
  totalMaxMarks: formData.questionType === QUESTION_TYPE_MULTIPLE_CHOICE
    ? formData.numQuestions
    : formData.totalMaxMarks,
  questionType: formData.questionType === GENERATION_TYPE_MIXED
    ? GENERATION_TYPE_MIXED
    : getEffectiveQuestionType(formData.questionType),
  difficulty: formData.difficulty,
  description: formData.description,
  extraInstructions: formData.extraInstructions,
});

export const normaliseMixedGeneratedBatch = (questions, total) => {
  const split = mixedQuestionSplit(total);
  if (!Array.isArray(questions) || questions.length !== total) {
    throw new Error(`The server did not return all ${total} mixed questions.`);
  }
  const counts = { multipleChoice: 0, shortAnswer: 0 };
  const normalised = questions.map((question, index) => {
    const type = question?.questionType;
    if (type !== QUESTION_TYPE_MULTIPLE_CHOICE && type !== QUESTION_TYPE_SHORT_ANSWER) {
      throw new Error(`Question ${index + 1} has an invalid question type.`);
    }
    counts[type] += 1;
    if (type === QUESTION_TYPE_MULTIPLE_CHOICE && question.maxMark !== 1) {
      throw new Error(`Question ${index + 1} has an invalid multiple-choice mark.`);
    }
    if (type === QUESTION_TYPE_SHORT_ANSWER &&
      (!Number.isInteger(question.maxMark) || question.maxMark < 1 ||
        !["markSchemeAnswer", "studentModelAnswer", "modelAnswer"].every(
          (field) => typeof question[field] === "string" && question[field].trim()
        ) || (question.options !== undefined && (!Array.isArray(question.options) || question.options.length > 0)) ||
        question.correctOptionId)) {
      throw new Error(`Question ${index + 1} has an invalid open-ended answer.`);
    }
    const result = normaliseGeneratedQuestion(question, index);
    if (type === QUESTION_TYPE_MULTIPLE_CHOICE) {
      const validation = validateMcqQuestion(result);
      if (!validation.isValid) throw new Error(`Question ${index + 1}: ${validation.errors.join(" ")}`);
    } else if (!result.questionText) {
      throw new Error(`Question ${index + 1} has an invalid open-ended answer.`);
    }
    return result;
  });
  if (counts.multipleChoice !== split.multipleChoice || counts.shortAnswer !== split.shortAnswer) {
    throw new Error(`The server returned the wrong mixed question split: expected ${split.multipleChoice} multiple-choice and ${split.shortAnswer} open-ended.`);
  }
  return normalised;
};

export const GeneratedMcqEditor = ({ question, onChange }) => (
  <div className="gqq-mcq-editor-wrap">
    <MultipleChoiceEditor
      options={question.options}
      correctOptionId={question.correctOptionId}
      onOptionsChange={(options) => onChange("options", options)}
      onCorrectOptionIdChange={(correctOptionId) => onChange("correctOptionId", correctOptionId)}
      errors={validateMcqQuestion(question).errors}
      idPrefix={`generated-${question._id}`}
    />
  </div>
);

const GenerateQuizQuestions = ({ user }) => {
  const navigate = useNavigate();

  // Form state
  const [formData, setFormData] = useState({
    subject: "",
    topic: "",
    level: "",
    specCode: "",
    numQuestions: 5,
    totalMaxMarks: 20,
    questionType: QUESTION_TYPE_SHORT_ANSWER,
    difficulty: "Medium",
    description: "",
    extraInstructions: ""
  });

  const [generatedQuestions, setGeneratedQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generationStatusIndex, setGenerationStatusIndex] = useState(0);
  const [addingQuestion, setAddingQuestion] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  // Save options state
  const [saveMode, setSaveMode] = useState(null);
  const [quizData, setQuizData] = useState({
    title: "",
    code: "",
    timer: ""
  });
  const [savingToFirestore, setSavingToFirestore] = useState(false);

  // Image upload state - track uploading per question
  const [uploadingImage, setUploadingImage] = useState(null); // _id of question currently uploading
  const fileInputRefs = useRef({});
  const creationRequestRef = useRef(null);
  const generationDialogRef = useRef(null);
  const writtenTotalMarksRef = useRef(20);

  // Subjects fetched from database
  const [subjects, setSubjects] = useState([]);
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem("darkMode") === "true");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [copiedTeacherCode, setCopiedTeacherCode] = useState(false);
  const [shellSearch, setShellSearch] = useState("");

  const role = user?.role || "teacher";
  const username = accountDisplayName(user, "StudyDen teacher");

  useEffect(() => {
    const previousScrollRestoration = window.history.scrollRestoration;
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }

    const scrollToPageTop = () => {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      document.scrollingElement?.scrollTo?.({ top: 0, left: 0, behavior: "auto" });
    };

    scrollToPageTop();
    const animationFrameId = window.requestAnimationFrame(scrollToPageTop);
    const timeoutId = window.setTimeout(scrollToPageTop, 0);

    return () => {
      window.cancelAnimationFrame(animationFrameId);
      window.clearTimeout(timeoutId);
      if ("scrollRestoration" in window.history) {
        window.history.scrollRestoration = previousScrollRestoration;
      }
    };
  }, []);

  useEffect(() => {
    document.body.classList.toggle("dark-mode", darkMode);
    document.documentElement.setAttribute("data-dark-mode", darkMode ? "true" : "false");
    localStorage.setItem("darkMode", JSON.stringify(darkMode));
  }, [darkMode]);

  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === "darkMode") setDarkMode(event.newValue === "true");
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  // Fetch subjects on mount
  const fetchSubjects = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, "questions"));
      const subjectsSet = new Set();
      querySnapshot.forEach((doc) => {
        const subject = doc.data().subject;
        if (subject) subjectsSet.add(subject);
      });
      setSubjects(Array.from(subjectsSet).sort());
    } catch (error) {
      console.error("Error fetching subjects: ", error.message);
    }
  };

  useEffect(() => {
    fetchSubjects();
  }, []);

  useEffect(() => {
    if (!loading) {
      setGenerationStatusIndex(0);
      return undefined;
    }

    setGenerationStatusIndex(0);
    const statusTimeoutIds = GENERATION_STATUS_STEPS.slice(1).map((step, index) => (
      window.setTimeout(() => setGenerationStatusIndex(index + 1), step.delay)
    ));

    return () => {
      statusTimeoutIds.forEach(timeoutId => window.clearTimeout(timeoutId));
    };
  }, [loading]);

  useEffect(() => {
    if (!loading) return undefined;

    const previouslyFocusedElement = document.activeElement;
    const previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const focusFrameId = window.requestAnimationFrame(() => {
      generationDialogRef.current?.focus();
    });

    return () => {
      window.cancelAnimationFrame(focusFrameId);
      document.body.style.overflow = previousBodyOverflow;

      if (previouslyFocusedElement instanceof HTMLElement && document.contains(previouslyFocusedElement)) {
        previouslyFocusedElement.focus();
      }
    };
  }, [loading]);

  // Compute total marks from generated questions
  const computedTotalMarks = generatedQuestions.reduce((sum, q) => sum + (parseInt(q.maxMark) || 0), 0);

  // Form validation
  const validateForm = () => {
    if (!formData.subject.trim()) { setError("Subject is required"); return false; }
    if (!formData.topic.trim()) { setError("Topic is required"); return false; }
    if (!formData.level.trim()) { setError("Level is required"); return false; }
    if (!formData.specCode.trim()) { setError("Spec Code is required"); return false; }
    if (formData.numQuestions < 1 || formData.numQuestions > 20) { setError("Number of questions must be between 1 and 20"); return false; }
    if (!formData.totalMaxMarks || formData.totalMaxMarks < 1 || formData.totalMaxMarks > 200) { setError("Total Max Marks must be between 1 and 200"); return false; }
    if (formData.totalMaxMarks < formData.numQuestions) { setError(`Total Max Marks (${formData.totalMaxMarks}) must be at least equal to the number of questions (${formData.numQuestions}).`); return false; }
    if (!formData.difficulty) { setError("Difficulty is required"); return false; }
    if (!formData.description.trim()) { setError("Description/Instructions are required"); return false; }
    setError("");
    return true;
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      if (name === "questionType") {
        if (value === QUESTION_TYPE_MULTIPLE_CHOICE) {
          if (prev.questionType !== QUESTION_TYPE_MULTIPLE_CHOICE) writtenTotalMarksRef.current = prev.totalMaxMarks;
          return { ...prev, questionType: value, totalMaxMarks: prev.numQuestions };
        }
        return {
          ...prev,
          questionType: value === GENERATION_TYPE_MIXED ? GENERATION_TYPE_MIXED : QUESTION_TYPE_SHORT_ANSWER,
          totalMaxMarks: prev.questionType === QUESTION_TYPE_MULTIPLE_CHOICE
            ? Math.max(prev.numQuestions, writtenTotalMarksRef.current || prev.numQuestions)
            : prev.totalMaxMarks,
        };
      }

      const parsedValue = (name === "numQuestions" || name === "totalMaxMarks") ? parseInt(value) || 0 : value;
      const next = { ...prev, [name]: parsedValue };
      if (name === "numQuestions" && prev.questionType === QUESTION_TYPE_MULTIPLE_CHOICE) {
        next.totalMaxMarks = parsedValue;
      }
      if (name === "totalMaxMarks" && prev.questionType !== QUESTION_TYPE_MULTIPLE_CHOICE) {
        writtenTotalMarksRef.current = parsedValue;
      }
      return next;
    });
  };

  // Call backend to generate questions (initial batch)
  const handleGenerateQuestions = async () => {
    if (!validateForm()) return;

    setLoading(true);
    setError("");
    setSuccessMessage("");
    setGeneratedQuestions([]);

    try {
      const auth = getAuth();
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error("You must be logged in to generate questions.");

      const idToken = await currentUser.getIdToken();
      const payload = buildGenerationPayload(formData);

      // Diagnostic logging to confirm full payload is being sent
      console.log("[GENERATE QUESTIONS] Sending payload:", payload);

      // Use a 120-second AbortController timeout on the fetch.
       // The backend has a 90-second internal guard, so this is intentionally
       // longer — the backend's own timeout response (success or 504 JSON)
       // will normally arrive before the frontend aborts.
       // A 5-question KS3 request that needs one validation retry can take ~60s.
       // Adding buffer ensures we get the backend's JSON error response rather than
       // the frontend AbortController timing out first.
       const controller = new AbortController();
       const frontendTimeoutId = setTimeout(() => controller.abort(), 120000);

      let response;
      try {
        response = await fetch(`${API_BASE_URL}/api/generate-quiz-questions`, {
          method: "POST",
          headers: { "Content-Type": "application/json", "Authorization": `Bearer ${idToken}` },
          body: JSON.stringify(payload),
          signal: controller.signal
        });
      } finally {
        clearTimeout(frontendTimeoutId);
      }

      const responseText = await response.text();

      // Detect HTML responses (e.g. nginx 504 Gateway Time-out pages) so the
      // user never sees raw HTML markup. We treat 504/502/503 and any HTML
      // body as a timeout-style error.
      const looksLikeHtml = /^\s*<(!doctype|html|head|body)/i.test(responseText);
      if (looksLikeHtml || response.status === 504 || response.status === 502 || response.status === 503) {
        throw new Error("Question generation took longer than expected. Please try again, or reduce the number of questions or marks slightly. If this keeps happening, the AI service may be busy.");
      }

      if (!response.ok) {
        let errorMessage = "Failed to generate questions";
        try {
          const parsed = JSON.parse(responseText);
          errorMessage = parsed.error || errorMessage;
        } catch (_) {
          // Non-JSON, non-HTML error body — use a safe generic message rather
          // than dumping raw text to the user.
          errorMessage = "Failed to generate questions. Please try again.";
        }
        throw new Error(errorMessage);
      }

      let data;
      try { data = JSON.parse(responseText); } catch (_) { throw new Error("Server returned an invalid response."); }
      if (!data || !Array.isArray(data.questions) || data.questions.length === 0) throw new Error("No questions were generated.");


      const questionsWithIds = formData.questionType === GENERATION_TYPE_MIXED
        ? normaliseMixedGeneratedBatch(data.questions, formData.numQuestions)
        : data.questions.map((q, idx) => normaliseGeneratedQuestion(q, idx));
      if (formData.questionType === QUESTION_TYPE_MULTIPLE_CHOICE) {
        questionsWithIds.forEach((question, index) => {
          const validation = validateMcqQuestion(question);
          if (!validation.isValid) throw new Error(`Question ${index + 1}: ${validation.errors.join(" ")}`);
        });
      }

      setGeneratedQuestions(questionsWithIds);
      const totalGenMarks = questionsWithIds.reduce((s, q) => s + (parseInt(q.maxMark) || 0), 0);
      setSuccessMessage(`Successfully generated ${questionsWithIds.length} question(s) — Total: ${totalGenMarks} marks`);
    } catch (err) {
      console.error("Error generating questions:", err);
      setError(err.message || "Failed to generate questions. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Add one additional question using the same context
  const handleAddAdditionalQuestion = async () => {
    setAddingQuestion(true);
    setError("");
    setSuccessMessage("");

    try {
      const auth = getAuth();
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error("You must be logged in.");

      const idToken = await currentUser.getIdToken();
      const additionalQuestionType = formData.questionType === GENERATION_TYPE_MIXED
        ? nextMixedQuestionType(generatedQuestions)
        : getEffectiveQuestionType(formData.questionType);
      const payload = {
        subject: formData.subject,
        topic: formData.topic,
        level: formData.level,
        specCode: formData.specCode,
        difficulty: formData.difficulty,
        description: formData.description,
        extraInstructions: formData.extraInstructions,
        questionType: additionalQuestionType,
        existingQuestions: generatedQuestions.map(q => ({
          questionText: q.questionText,
          maxMark: parseInt(q.maxMark) || 1,
          questionType: getEffectiveQuestionType(q.questionType),
        }))
      };

      const response = await fetch(`${API_BASE_URL}/api/generate-additional-question`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${idToken}` },
        body: JSON.stringify(payload)
      });

      const responseText = await response.text();
      const looksLikeHtml = /^\s*<(!doctype|html|head|body)/i.test(responseText);
      if (looksLikeHtml || response.status === 504 || response.status === 502 || response.status === 503) {
        throw new Error("The request timed out. Please try again.");
      }
      if (!response.ok) {
        let errorMessage = "Failed to generate additional question";
        try {
          const parsed = JSON.parse(responseText);
          errorMessage = parsed.error || errorMessage;
        } catch (_) {
          errorMessage = "Failed to generate additional question. Please try again.";
        }
        throw new Error(errorMessage);
      }


      let data;
      try { data = JSON.parse(responseText); } catch (_) { throw new Error("Server returned an invalid response."); }
      if (!data || !data.question) throw new Error("No question was generated.");

      const q = data.question;
      if (formData.questionType === GENERATION_TYPE_MIXED && q.questionType !== additionalQuestionType) {
        throw new Error("The server returned the wrong question type.");
      }
      if (formData.questionType === GENERATION_TYPE_MIXED && additionalQuestionType === QUESTION_TYPE_SHORT_ANSWER &&
        (typeof q.questionText !== "string" || !q.questionText.trim() ||
          typeof q.markSchemeAnswer !== "string" || !q.markSchemeAnswer.trim() ||
          !Number.isInteger(q.maxMark) || q.maxMark < 1 || q.options?.length || q.correctOptionId)) {
        throw new Error("The server returned an invalid open-ended question.");
      }
      const newQuestion = normaliseGeneratedQuestion(q, generatedQuestions.length, "gen_add");
      if (newQuestion.questionType === QUESTION_TYPE_MULTIPLE_CHOICE) {
        const validation = validateMcqQuestion(newQuestion);
        if (!validation.isValid) throw new Error(validation.errors.join(" "));
      }
      const maxMark = newQuestion.maxMark;

      setGeneratedQuestions(prev => [...prev, newQuestion]);
      setSuccessMessage(`Added Question ${generatedQuestions.length + 1} (${maxMark} mark${maxMark === 1 ? "" : "s"})`);
    } catch (err) {
      console.error("Error adding question:", err);
      setError(err.message || "Failed to add question.");
    } finally {
      setAddingQuestion(false);
    }
  };

   // Handle per-question image upload
   const handleImageUpload = async (questionId, file) => {
     if (!file) return;

     setUploadingImage(questionId);
     setError("");

     try {
       const storageRef = ref(storage, `question-images/${Date.now()}_${file.name}`);
       await uploadBytes(storageRef, file);
       const url = await getDownloadURL(storageRef);

       setGeneratedQuestions(prev =>
         prev.map(q => q._id === questionId ? { ...q, questionImageURL: url } : q)
       );
       
       // Show success message for image upload
       setSuccessMessage("Image attached successfully!");
       setTimeout(() => setSuccessMessage(""), 3000);
     } catch (err) {
       console.error("Error uploading image:", err);
       setError(`Failed to upload image: ${err.message}`);
     } finally {
       setUploadingImage(null);
     }
   };

  // Remove image from question
  const handleRemoveImage = (questionId) => {
    setGeneratedQuestions(prev =>
      prev.map(q => q._id === questionId ? { ...q, questionImageURL: "" } : q)
    );
  };

  // Update generated question
  const handleUpdateQuestion = (id, field, value) => {
    setGeneratedQuestions((prev) =>
      prev.map((q) => {
        if (q._id !== id) return q;

        if (field === "maxMark") {
          if (q.questionType === QUESTION_TYPE_MULTIPLE_CHOICE) return { ...q, maxMark: 1 };
          const nextMaxMark = parseInt(value, 10) || 1;
          return {
            ...q,
            maxMark: nextMaxMark,
            questionText: ensureMarkTag(q.questionText, nextMaxMark)
          };
        }

        return { ...q, [field]: value };
      })
    );
  };

  const handleQuestionTextBlur = (id) => {
    setGeneratedQuestions((prev) =>
      prev.map((q) =>
        q._id === id && q.questionType !== QUESTION_TYPE_MULTIPLE_CHOICE
          ? { ...q, questionText: ensureMarkTag(q.questionText, q.maxMark) }
          : q
      )
    );
  };

  // Delete generated question
  const handleDeleteQuestion = (id) => {
    setGeneratedQuestions((prev) => prev.filter((q) => q._id !== id));
  };

  // Validate all questions before save
  const validateQuestionsBeforeSave = () => {
    if (generatedQuestions.length === 0) { setError("No questions to save."); return false; }
    for (let i = 0; i < generatedQuestions.length; i++) {
      const q = normaliseGeneratedQuestion(generatedQuestions[i], i);
      if (q.questionType === QUESTION_TYPE_MULTIPLE_CHOICE) {
        const validation = validateMcqQuestion(q);
        if (!validation.isValid) { setError(`Question ${i + 1}: ${validation.errors.join(" ")}`); return false; }
        continue;
      }
      if (!q.questionText) { setError(`Question ${i + 1}: Question text is required`); return false; }
      if (!normaliseMarkSchemeAnswer(q.markSchemeAnswer)) { setError(`Question ${i + 1}: Mark scheme answer is required`); return false; }
      if (!q.maxMark || isNaN(q.maxMark) || q.maxMark <= 0) { setError(`Question ${i + 1}: Max mark must be a positive number`); return false; }
    }
    setError("");
    return true;
  };

  // Validate quiz data
  const validateQuizData = () => {
    if (!quizData.title.trim()) { setError("Test title is required"); return false; }
    if (!quizData.code.trim()) { setError("Test code is required"); return false; }
    if (!quizData.timer || isNaN(quizData.timer) || quizData.timer <= 0) { setError("Timer must be a positive number (minutes)"); return false; }
    setError("");
    return true;
  };

  // Save questions to Firestore
  const handleSaveQuestions = async () => {
    if (!user?.uid) {
      setError("You must be logged in to save questions.");
      return;
    }
    if (!validateQuestionsBeforeSave()) return;

    setSavingToFirestore(true);
    setError("");
    setSuccessMessage("");

    try {
      const savedQuestionIds = [];
      const questionsToSave = generatedQuestions.map((q, idx) => {
        const questionDoc = {
          ...buildGeneratedQuestionFields(q, formData),
          createdAt: serverTimestamp(),
          createdBy: user.uid
        };
        return questionDoc;
      });

      console.log("Questions being saved:", questionsToSave);

      for (const questionDoc of questionsToSave) {
        const docRef = await addDoc(collection(db, "questions"), questionDoc);
        savedQuestionIds.push(docRef.id);
      }

      setSuccessMessage(`Successfully saved ${savedQuestionIds.length} question(s)!`);

      if (saveMode === "questionsOnly") {
        setTimeout(() => {
          setFormData({ subject: "", topic: "", level: "", specCode: "", numQuestions: 5, totalMaxMarks: 20, questionType: QUESTION_TYPE_SHORT_ANSWER, difficulty: "Medium", description: "", extraInstructions: "" });
          setGeneratedQuestions([]);
          setSaveMode(null);
        }, 2000);
      } else if (saveMode === "questionsAndQuiz") {
        await handleCreateQuiz(savedQuestionIds);
      }
    } catch (err) {
      console.error("Error saving questions:", err);
      setError("Failed to save questions. Please try again.");
    } finally {
      setSavingToFirestore(false);
    }
  };

  // Create quiz with saved questions
  const handleCreateQuiz = async (questionIds) => {
    if (!validateQuizData()) return;

    console.log(`[QUIZ CREATE] Creating quiz for teacher UID: ${user.uid}`);
    console.log(`[QUIZ CREATE] Requested quiz code: ${quizData.code}`);

    try {
      const timeLimitInSeconds = parseInt(quizData.timer) * 60;

      const quizDoc = {
        quizCode: quizData.code,
        title: quizData.title,
        questionIds: questionIds,
        defaultTimeLimit: timeLimitInSeconds,
        subject: formData.subject,
      };

      // The backend assigns the quiz ID and reserves the exact student code.
      const created = await createQuizApi("generated", quizDoc, creationRequestRef);

      console.log(`[QUIZ CREATE] Quiz created successfully: ${created.quizId}`);

      setSuccessMessage(`Test "${quizData.title}" created successfully! Test Code: ${created.quizCode}`);
      
      // Add specCode and subject to each question for the success page
      const questionsWithMetadata = generatedQuestions.map(q => ({
        ...q,
        specCode: formData.specCode,
        subject: formData.subject
      }));
      
      navigate("/quiz-success", {
        state: { quizCode: created.quizCode, quizTitle: quizData.title, selectedQuestions: questionsWithMetadata },
      });
    } catch (err) {
      console.error("Error creating quiz:", err);
      setError(err.code === "code-collision"
        ? "This test code is already used or retired. Please choose a different code."
        : err.message || "Failed to create test. Please try again.");
    }
  };

  const handleLogout = async () => {
    try {
      await getAuth().signOut();
      navigate("/");
    } catch (logoutError) {
      console.error("Error signing out", logoutError);
    }
  };

  const handleCopyTeacherCode = async () => {
    if (!user?.teacherCode) return;
    try {
      await navigator.clipboard.writeText(user.teacherCode);
      setCopiedTeacherCode(true);
      window.setTimeout(() => setCopiedTeacherCode(false), 1800);
    } catch (copyError) {
      console.error("Could not copy teacher code", copyError);
    }
  };

  return (
    <>
    <div className={`sd-dashboard sd-brand-theme sd-role-${role} gqq-page${darkMode ? " sd-dark" : ""}`}
      inert={loading ? "" : undefined} aria-busy={loading}>
      <DashboardSidebar role={role} username={username} officialDashboardBrand
        creditsRemaining={role === "admin" ? "unlimited" : user?.creditsRemaining ?? null}
        teacherCode={user?.teacherCode || ""} onCopyCode={handleCopyTeacherCode}
        copied={copiedTeacherCode} onLogout={handleLogout} open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        activePath="/generate-quiz-questions" />
      <div className="sd-workspace gqq-workspace">
        <DashboardHeader username={username} role={role} darkMode={darkMode}
          onToggleTheme={() => setDarkMode((value) => !value)}
          onOpenMenu={() => setSidebarOpen(true)} search={shellSearch} onSearch={setShellSearch} />
        <main className="sd-main generate-quiz-main">
          <section className="gqq-hero">
            <div className="gqq-hero-copy">
              <button type="button" className="gqq-hero-back" onClick={() => navigate("/dashboard")}>
                <ArrowLeft size={15} />Dashboard
              </button>
              <p className="gqq-eyebrow">AI question generator</p>
              <h1>Generate Test Questions</h1>
              <p>Create assessment-ready questions from your topic and teaching requirements with AI.</p>
              <div className="gqq-step-strip" aria-label="Question generation workflow">
                <span><strong>1</strong>Source</span><span><strong>2</strong>Settings</span>
                <span><strong>3</strong>Generate</span><span><strong>4</strong>Review</span><span><strong>5</strong>Use</span>
              </div>
            </div>
            <div className="gqq-hero-art" aria-hidden="true">
              <span className="gqq-art-orbit"><Sparkles size={21} /></span>
              <span className="gqq-art-card gqq-art-card-back"><BookOpenCheck size={28} /></span>
              <span className="gqq-art-card gqq-art-card-front"><WandSparkles size={38} /></span>
              <i className="gqq-hero-arc gqq-hero-arc-one" /><i className="gqq-hero-arc gqq-hero-arc-two" />
            </div>
          </section>
          <div className="gqq-content">
        {error && <div className="message error-message" role="alert"><ClipboardCheck size={18} /><div><strong>Action needed</strong><p>{error}</p></div></div>}
        {successMessage && <div className="message success-message" role="status" aria-live="polite"><CheckCircle2 size={18} /><div><strong>Ready</strong><p>{successMessage}</p></div></div>}

        {/* Section 1: Generation Form */}
        {generatedQuestions.length === 0 && (
          <section className="form-section card">
            <div className="gqq-section-heading"><span><Sparkles size={20} /></span><div><p>Configure generation</p><h2>Build your question set</h2><small>Give StudyDen the teaching context and assessment settings it needs.</small></div></div>
            <div className="gqq-form-section-label"><span>01</span><div><strong>Source &amp; topic</strong><small>Describe the subject matter and the questions you need.</small></div></div>

            <div className="form-grid">
              <div className="form-group gqq-source-field">
                <label htmlFor="subject">Subject *</label>
                <input 
                  id="subject" 
                  type="text" 
                  name="subject" 
                  list="subject-list"
                  placeholder="e.g., Computer Science, Maths, History" 
                  value={formData.subject} 
                  onChange={handleInputChange} 
                  disabled={loading}
                  autoComplete="off"
                />
                <datalist id="subject-list">
                  {subjects.map((s) => (<option key={s} value={s} />))}
                </datalist>
                <span className="field-hint">Select from list or type a new subject</span>
              </div>
              <div className="form-group gqq-source-field">
                <label htmlFor="topic">Topic *</label>
                <input id="topic" type="text" name="topic" placeholder="e.g., Variables and Data Types" value={formData.topic} onChange={handleInputChange} disabled={loading} />
              </div>
              <div className="form-group full-width gqq-source-field">
                <label htmlFor="description">Description / Instructions *</label>
                <textarea id="description" name="description" placeholder="Describe the type of questions you want..." value={formData.description} onChange={handleInputChange} disabled={loading} rows="4" />
              </div>
              <div className="form-group full-width gqq-source-field">
                <label htmlFor="extraInstructions">Extra Instructions (Optional)</label>
                <textarea id="extraInstructions" name="extraInstructions" placeholder="Any additional requirements..." value={formData.extraInstructions} onChange={handleInputChange} disabled={loading} rows="3" />
              </div>
              <div className="gqq-form-section-label gqq-settings-label"><span>02</span><div><strong>Question settings</strong><small>Set the level, size, marks, and challenge.</small></div></div>
              <div className="form-group gqq-settings-field">
                <label htmlFor="level">Level *</label>
                <input id="level" type="text" name="level" placeholder="e.g., GCSE, A-Level, KS3" value={formData.level} onChange={handleInputChange} disabled={loading} />
              </div>
              <div className="form-group gqq-settings-field">
                <label htmlFor="specCode">Spec Code *</label>
                <input id="specCode" type="text" name="specCode" placeholder="e.g., OCR-CS-1.2 or AQA-8525-3.1" value={formData.specCode} onChange={handleInputChange} disabled={loading} />
              </div>
              <div className="form-group gqq-settings-field">
                <label htmlFor="questionType">Question Type *</label>
                <select id="questionType" name="questionType" value={formData.questionType} onChange={handleInputChange} disabled={loading}>
                  <option value={QUESTION_TYPE_SHORT_ANSWER}>Short Answer</option>
                  <option value={QUESTION_TYPE_MULTIPLE_CHOICE}>Multiple Choice</option>
                  <option value={GENERATION_TYPE_MIXED}>Mixed (multiple choice and open-ended)</option>
                </select>
                {formData.questionType === GENERATION_TYPE_MIXED && (
                  <span className="field-hint">
                    {formData.numQuestions === 1
                      ? "With one question, Mixed generates one multiple-choice question worth 1 mark and no open-ended questions."
                      : `${mixedQuestionSplit(formData.numQuestions).multipleChoice} multiple-choice and ${mixedQuestionSplit(formData.numQuestions).shortAnswer} open-ended questions; an odd extra goes to multiple choice.`}
                  </span>
                )}
              </div>
              <div className="form-group gqq-settings-field">
                <label htmlFor="numQuestions">Number of Questions (1-20) *</label>
                <input id="numQuestions" type="number" name="numQuestions" min="1" max="20" value={formData.numQuestions} onChange={handleInputChange} disabled={loading} />
              </div>
              <div className="form-group gqq-settings-field">
                 <label htmlFor="totalMaxMarks">Total Max Marks *</label>
                 <input id="totalMaxMarks" type="number" name="totalMaxMarks" min="1" max="200" placeholder="e.g., 30" value={formData.totalMaxMarks} onChange={handleInputChange} disabled={loading || formData.questionType === QUESTION_TYPE_MULTIPLE_CHOICE} />
                 <span className="field-hint">
                   {formData.questionType === QUESTION_TYPE_MULTIPLE_CHOICE
                     ? "Multiple-choice questions are worth 1 mark each."
                     : "Questions may use fewer marks if that creates a better assessment"}
                 </span>
              </div>
              <div className="form-group gqq-settings-field">
                <label htmlFor="difficulty">Difficulty *</label>
                <select id="difficulty" name="difficulty" value={formData.difficulty} onChange={handleInputChange} disabled={loading}>
                  {DIFFICULTY_LEVELS.map((d) => (<option key={d} value={d}>{d}</option>))}
                </select>
              </div>
            </div>

            <div className="button-group gqq-generate-bar">
              <div><strong>Ready to create your questions?</strong><span>{role === "teacher" ? "A successful generation uses 1 credit." : "Review your settings before generating."}</span></div>
              <button type="button" className="primary-button generate-button" onClick={handleGenerateQuestions} disabled={loading}>
                <WandSparkles size={18} />{loading ? "Generating..." : "Generate Questions"}
              </button>
            </div>
          </section>
        )}

        {/* Section 2: Review & Edit Questions */}
        {generatedQuestions.length > 0 && (
          <section className="review-section card">
            <div className="gqq-review-heading">
              <div className="gqq-section-heading"><span><ClipboardCheck size={20} /></span><div><p>Review questions</p><h2>Generated Questions</h2><small>Review and refine every question before adding it to your Question Bank or test.</small></div></div>
              <button type="button" className="gqq-text-action" onClick={() => { setGeneratedQuestions([]); setSaveMode(null); }} disabled={savingToFirestore}><ArrowLeft size={15} />Generate another set</button>
            </div>

            {/* Summary Bar */}
            <div className="generation-summary">
              <div className="summary-item">
                <span className="summary-label">Questions</span>
                <span className="summary-value">{generatedQuestions.length}</span>
              </div>
              <div className="summary-item">
                <span className="summary-label">Total Marks</span>
                <span className="summary-value">{computedTotalMarks}</span>
              </div>
              <div className="summary-item">
                <span className="summary-label">Subject</span>
                <span className="summary-value">{formData.subject}</span>
              </div>
              <div className="summary-item">
                <span className="summary-label">Spec Code</span>
                <span className="summary-value">{formData.specCode}</span>
              </div>
            </div>

            <div className="questions-container">
              {generatedQuestions.map((q, index) => (
                <article key={q._id} className="question-card">
                  <div className="question-card-header">
                    <div className="question-heading-main">
                      <span className="question-number">Q{index + 1}</span>
                      <strong>Question {index + 1}</strong>
                    </div>
                    <div className="question-header-meta">
                      <span className="question-type-badge">{questionTypeDisplayLabel(q.questionType)}</span>
                      <span className="mark-badge">
                        {parseInt(q.maxMark) === 1 ? "1 Mark" : `${q.maxMark} Marks`}
                      </span>
                    </div>
                  </div>

                  {/* Question Text */}
                  <div className="question-field">
                    <label htmlFor={`question-text-${q._id}`}>Question Text</label>
                    <textarea
                      id={`question-text-${q._id}`}
                      value={q.questionText}
                      onChange={(e) => handleUpdateQuestion(q._id, "questionText", e.target.value)}
                      onBlur={() => handleQuestionTextBlur(q._id)}
                      rows="3"
                      placeholder="Question text..."
                    />
                  </div>

                   {q.questionType === QUESTION_TYPE_MULTIPLE_CHOICE ? (
                     <GeneratedMcqEditor
                       question={q}
                       onChange={(field, value) => handleUpdateQuestion(q._id, field, value)}
                     />
                   ) : (
                     <>
                       {/* Mark Scheme Answer */}
                       <div className="question-field mark-scheme-field">
                         <label htmlFor={`mark-scheme-${q._id}`}><ClipboardCheck size={15} />Mark Scheme Answer</label>
                         <textarea id={`mark-scheme-${q._id}`} value={q.markSchemeAnswer} onChange={(e) => handleUpdateQuestion(q._id, "markSchemeAnswer", e.target.value)} rows="3" placeholder="Mark scheme..." />
                       </div>

                       {/* Model Answer */}
                       <div className="question-field model-answer-field">
                         <label htmlFor={`model-answer-${q._id}`}><CheckCircle2 size={15} />Model Answer (Full-Mark Response)</label>
                         <textarea id={`model-answer-${q._id}`} value={q.modelAnswer || ""} onChange={(e) => handleUpdateQuestion(q._id, "modelAnswer", e.target.value)} rows="3" placeholder="Example of a correct full-mark answer..." />
                       </div>
                     </>
                   )}

                   {/* Max Mark */}
                   <div className="question-field inline-field">
                     <label htmlFor={`max-mark-${q._id}`}>Max Mark</label>
                     <input id={`max-mark-${q._id}`} type="number" min="1" value={q.maxMark} onChange={(e) => handleUpdateQuestion(q._id, "maxMark", e.target.value)} placeholder="0" className="max-mark-input" disabled={q.questionType === QUESTION_TYPE_MULTIPLE_CHOICE} />
                     {q.questionType === QUESTION_TYPE_MULTIPLE_CHOICE && <span className="field-hint">Multiple-choice questions are fixed at 1 mark.</span>}
                   </div>

                  {/* Image Upload Section */}
                  <div className="question-image-section">
                    <label>
                      Question Image / Diagram (Optional)
                      {q.questionImageURL && <span className="image-attached-badge"><CheckCircle2 size={13} />Image Attached</span>}
                    </label>
                    {q.questionImageURL ? (
                      <div className="image-preview-container">
                        <img src={q.questionImageURL} alt={`Q${index + 1} diagram`} className="question-image-preview" />
                        <button type="button" className="remove-image-button" onClick={() => handleRemoveImage(q._id)} title="Remove image"><Trash2 size={14} />Remove Image</button>
                      </div>
                    ) : (
                      <div className="image-upload-row">
                        <input
                          type="file"
                          accept="image/*"
                          aria-label={`Attach an image to question ${index + 1}`}
                          ref={el => { fileInputRefs.current[q._id] = el; }}
                          style={{ display: "none" }}
                          onChange={(e) => {
                            if (e.target.files[0]) handleImageUpload(q._id, e.target.files[0]);
                          }}
                        />
                        <button
                          className="upload-image-button"
                          onClick={() => fileInputRefs.current[q._id]?.click()}
                          disabled={uploadingImage === q._id}
                        >
                          {uploadingImage === q._id ? (
                            <><LoaderCircle size={16} className="gqq-spin" />Uploading...</>
                          ) : (
                            <><ImagePlus size={16} />Attach Image</>
                          )}
                        </button>
                        <span className="image-hint">PNG, JPG — diagrams, charts, flowcharts, etc.</span>
                      </div>
                    )}
                  </div>

                  {/* Delete Button */}
                  <button type="button" className="delete-button" onClick={() => handleDeleteQuestion(q._id)} title="Delete this question">
                    <Trash2 size={15} />Remove Question
                  </button>
                </article>
              ))}
            </div>

            {/* Add Additional Question Button */}
            <div className="add-question-section">
              <button
                type="button"
                className="add-question-button"
                onClick={handleAddAdditionalQuestion}
                disabled={addingQuestion || savingToFirestore}
              >
                {addingQuestion ? (
                  <><LoaderCircle size={16} className="gqq-spin" />Generating Question...</>
                ) : (
                  <><Sparkles size={16} />Add Additional Question</>
                )}
              </button>
              <span className="add-question-hint">
                Generates one new question using the same topic and settings{role === "teacher" ? " and uses 1 credit when successful" : ""}
              </span>
            </div>

            {/* Save Options */}
            {generatedQuestions.length > 0 && (
              <div className="save-options-section">
                <div className="gqq-save-heading"><span><Save size={19} /></span><div><p>Use questions</p><h3>Save your question set</h3><small>Keep the questions in your bank or create a ready-to-use test.</small></div></div>

                {saveMode === null && (
                  <div className="mode-buttons">
                    <button type="button" className="secondary-button" onClick={() => setSaveMode("questionsOnly")} disabled={savingToFirestore}>
                      <Save size={17} />Save to Question Bank
                    </button>
                    <button type="button" className="primary-button" onClick={() => setSaveMode("questionsAndQuiz")} disabled={savingToFirestore}>
                      <BookOpenCheck size={17} />Save &amp; Create Test
                    </button>
                  </div>
                )}

                {saveMode === "questionsOnly" && (
                  <div className="save-confirmation">
                    <p>Save {generatedQuestions.length} question(s) totalling {computedTotalMarks} marks to your question bank?</p>
                    <div className="confirmation-buttons">
                      <button type="button" className="primary-button" onClick={handleSaveQuestions} disabled={savingToFirestore}>
                        {savingToFirestore ? "Saving..." : "Confirm & Save"}
                      </button>
                      <button type="button" className="secondary-button" onClick={() => setSaveMode(null)} disabled={savingToFirestore}>Cancel</button>
                    </div>
                  </div>
                )}

                {saveMode === "questionsAndQuiz" && (
                  <div className="quiz-creation-form">
                    <p className="form-intro">Fill in the test details below. Your questions will be saved first, then the test will be created.</p>
                    <div className="form-grid">
                      <div className="form-group">
                        <label htmlFor="quizTitle">Test Title *</label>
                        <input id="quizTitle" type="text" placeholder="e.g., Biology Unit 3 Assessment" value={quizData.title} onChange={(e) => setQuizData(prev => ({ ...prev, title: e.target.value }))} disabled={savingToFirestore} />
                      </div>
                      <div className="form-group">
                        <label htmlFor="quizCode">Test Code *</label>
                        <input id="quizCode" type="text" placeholder="e.g., BIO3Q1" value={quizData.code} onChange={(e) => setQuizData(prev => ({ ...prev, code: e.target.value }))} disabled={savingToFirestore} />
                      </div>
                      <div className="form-group">
                        <label htmlFor="quizTimer">Timer (minutes) *</label>
                        <input id="quizTimer" type="number" min="1" placeholder="e.g., 45" value={quizData.timer} onChange={(e) => setQuizData(prev => ({ ...prev, timer: e.target.value }))} disabled={savingToFirestore} />
                      </div>
                    </div>
                    <div className="confirmation-buttons">
                      <button type="button" className="primary-button" onClick={handleSaveQuestions} disabled={savingToFirestore}>
                        {savingToFirestore ? "Creating..." : "Save & Create Test"}
                      </button>
                      <button type="button" className="secondary-button" onClick={() => setSaveMode(null)} disabled={savingToFirestore}>Cancel</button>
                    </div>
                  </div>
                )}
              </div>
            )}

          </section>
        )}
          </div>
        </main>
      </div>
    </div>

    {loading && (
      <div className="generation-loading-overlay">
        <div
          className="generation-loading-dialog"
          ref={generationDialogRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="generation-loading-title"
          aria-describedby="generation-loading-help"
          tabIndex="-1"
        >
          <div className="generation-loading-indicator" aria-hidden="true">
            <span className="generation-loading-ring"></span>
            <span className="generation-loading-core"><WandSparkles size={25} /></span>
          </div>
          <span className="generation-loading-eyebrow">Generating questions</span>
          <h2 id="generation-loading-title">Creating assessment-ready questions...</h2>
          <p
            className="generation-loading-status"
            id="generation-loading-status"
            key={generationStatusIndex}
            role="status"
            aria-live="polite"
            aria-atomic="true"
          >
            {GENERATION_STATUS_STEPS[generationStatusIndex].message}
          </p>
          <p className="generation-loading-help" id="generation-loading-help">
            Complex assessments can take a little longer. Please keep this page open.
          </p>
        </div>
      </div>
    )}
    </>
  );
};

export default GenerateQuizQuestions;
